import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'; // Adiciona useRef e useMemo
import { Button, Row, Col, Image } from 'react-bootstrap'; // Importa Table, Row, Col, Image, Tabs, Tab, Card
import axios from 'axios';
import { useEntidadeContext } from '../EntidadeContext'; // Importar contexto da entidade
import { useUserContext } from '../UserContext'; // Importar o contexto do usuário
import ServicosTab from '../components/servicos/ServicosTab';
import logoImage from '../img/logo.png'; // Importa a imagem da logo
import { useAlertModal } from '../AlertModalContext';

const Servicos = () => {
    const { currentUserRole, getPermittedInternalTabs } = useUserContext(); // Obter do contexto
    const { showAlertModal } = useAlertModal();
    const [servicos, setServicos] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMaterialData, setIsLoadingMaterialData] = useState(false); // Novo estado de loading para materiais
    const [tiposMateriais, setTiposMateriais] = useState([]);
    const [materiais, setMateriais] = useState([]);
    const [selectedMaterialsData, setSelectedMaterialsData] = useState([]);
    const [currentServico, setCurrentServico] = useState(null); // Novo estado para edição
    const {currentEntidade, isLoadingEntidade} = useEntidadeContext(); // Usar contexto
    const [mostrarInativos, setMostrarInativos] = useState(false); // Novo estado para filtro


    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        valor: '',
        percentual_comissao: '',
        numero_sessoes: 1,
        custo_material: '',
        tipos_materiais: [],
    });

    const [custoMaterialCalculado, setCustoMaterialCalculado] = useState(0); // Add state for calculated cost

    // Obter abas permitidas para Serviços
    const permittedTabs = useMemo(() =>
        getPermittedInternalTabs('servicos', currentUserRole),
      [getPermittedInternalTabs, currentUserRole]
    );

    // Função centralizada para buscar dados, considerando o filtro
    const fetchData = useCallback(async () => {
        if (!currentEntidade) return;

        setIsLoading(true);
        try {
            const status = mostrarInativos ? 'inativo' : 'ativo';
            // Envia o status para o backend. Se o backend não filtrar, filtramos no frontend.
            const servicosResponse = await axios.get(`/api/servicos?entidade_id=${currentEntidade.id}&status=${status}`);
            
            let listaServicos = servicosResponse.data.servicos || [];
            
            // Filtragem no frontend para garantir (caso o backend retorne tudo)
            listaServicos = listaServicos.filter(s => mostrarInativos ? !s.ativo : s.ativo);

            setServicos(listaServicos);
        } catch (error) {
            console.error("Erro ao buscar dados para Serviços:", error);
        } finally {
            setIsLoading(false);
        }
    }, [currentEntidade, mostrarInativos]);

    useEffect(() => {
        if (currentEntidade && !isLoadingEntidade) {
            fetchData();
        }
    }, [currentEntidade, isLoadingEntidade, fetchData]);

    // Novo useEffect para buscar tipos de materiais e materiais apenas quando o modal é aberto
    useEffect(() => {
        const fetchMaterialData = async () => {
            if (!currentEntidade || !showModal) {
                return;
            }

            // Se os dados de materiais já foram carregados, não busca novamente.
            if (tiposMateriais.length > 0 && materiais.length > 0) {
                return;
            }

            setIsLoadingMaterialData(true);
            try {
                const [tiposMateriaisResponse, materiaisResponse] = await Promise.all([
                    axios.get(`/api/tipos_materiais?entidade_id=${currentEntidade.id}`),
                    axios.get(`/api/materiais-com-tipo?entidade_id=${currentEntidade.id}`)
                ]);
                setTiposMateriais(tiposMateriaisResponse.data.tipos_materiais);
                setMateriais(materiaisResponse.data.materiais);
            } catch (error) {
                console.error("Erro ao buscar dados de materiais:", error);
                // Opcional: showAlertModal('Erro', 'Não foi possível carregar os dados de materiais.', 'danger');
                setTiposMateriais([]);
                setMateriais([]);
            } finally {
                setIsLoadingMaterialData(false);
            }
        };

        fetchMaterialData();
    }, [showModal, currentEntidade]); // Depende de showModal e currentEntidade

        // useEffect separado para calcular o custo do material quando selectedMaterialsData ou materiais mudam
    useEffect(() => {
        let newCustoMaterial = 0;
        selectedMaterialsData.forEach(materialItem => {
            const materialInfo = materiais.find(m => m.id === materialItem.id);
            if (materialInfo && materialInfo.valor_unidade) { // Verifica se valor_unidade existe
                newCustoMaterial += materialItem.quantidade_utilizada * parseFloat(materialInfo.valor_unidade);
            }
        });
        setCustoMaterialCalculado(newCustoMaterial.toFixed(2)); // Garante 2 casas decimais
    }, [selectedMaterialsData, materiais]); // Depende dos dados selecionados e da lista de materiais

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentServico(null); // Limpa o serviço em edição ao fechar
        setFormData({
            nome: '',
            descricao: '',
            valor: '',
            numero_sessoes: 1,
            custo_material: '',
            tipos_materiais: [],
        });
        setSelectedMaterialsData([]);
    };

    const handleOpenModal = (servico = null) => {
        if (servico) {
            // Modo de edição: preenche o formulário com os dados do serviço
            setCurrentServico(servico);
            const materiaisVinculados = servico.materiais?.map(m => ({
                id: m.id,
                quantidade_utilizada: m.pivot.quantidade_utilizada
            })) || [];
            const tiposMateriaisIds = servico.materiais?.map(m => m.materiais_tipos_id) || [];

            setFormData({
                nome: servico.nome,
                descricao: servico.descricao,
                valor: servico.valor,
                percentual_comissao: servico.percentual_comissao || '',
                numero_sessoes: servico.numero_sessoes || 1,
                custo_material: servico.custo_material,
                tipos_materiais: [...new Set(tiposMateriaisIds)], // Pega IDs únicos dos tipos de materiais
            });
            setSelectedMaterialsData(materiaisVinculados);
        } else {
            // Modo de criação: reseta o formulário
            setCurrentServico(null);
            setFormData({
                nome: '',
                descricao: '',
                valor: '',
                percentual_comissao: '',
                numero_sessoes: 1,
                custo_material: '',
                tipos_materiais: [],
            });
            setSelectedMaterialsData([]);
        }
        setShowModal(true);
    };

    const handleChange = (e) => {
        if (e.target.name === "tipos_materiais") {
            const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value, 10));
            setFormData({ ...formData, tipos_materiais: selectedOptions });
        } else {
            setFormData({ ...formData, [e.target.name]: e.target.value });
        }
    };

    const handleMaterialChange = (materialId, checked) => {  // Add 'checked' parameter
        setSelectedMaterialsData(prevSelectedMaterials => {
          if (checked) {
            // Material is checked, add it to the selectedMaterialsData (with quantity 0 initially)
            return [...prevSelectedMaterials, { id: materialId, quantidade_utilizada: 0 }];
          } else {
            // Material is unchecked, remove it
            return prevSelectedMaterials.filter(material => material.id !== materialId);
          }
        });
      };

      const handleQuantidadeChange = (materialId, quantidadeUtilizada) => {

        setSelectedMaterialsData(prevSelectedMaterials => {

            const updatedMaterials = prevSelectedMaterials.map(material => {
                if (material.id === materialId) {
                    return { ...material, quantidade_utilizada: quantidadeUtilizada };
                }
                return material;
            });

            // Calculate total material cost
            let newCustoMaterial = 0;

            updatedMaterials.forEach(materialItem => {

                const materialInfo = materiais.find(m => m.id === materialItem.id);

                if (materialInfo) {
                    newCustoMaterial += materialItem.quantidade_utilizada * parseFloat(materialInfo.valor_unidade);
                }
            });
            setCustoMaterialCalculado(newCustoMaterial.toFixed(2)); // Update calculated cost state
            return updatedMaterials;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validação Frontend: Materiais Obrigatórios
        if (selectedMaterialsData.length === 0) {
            showAlertModal('Atenção', 'É obrigatório vincular pelo menos um material ao serviço.', 'warning');
            return;
        }

        setIsLoading(true);
        if (!currentEntidade) return; // Segurança

        try {
            const data = {
                ...formData,
                entidade_id: currentEntidade.id, // Adiciona o entidade_id do contexto
                materiais: selectedMaterialsData,
                custo_material: custoMaterialCalculado,
            };

            if (currentServico) {
                // Atualizando um serviço existente
                await axios.put(`/api/servicos/${currentServico.id}`, data);
            } else {
                // Criando um novo serviço
                await axios.post(`/api/servicos`, data);
            }

            // Recarrega os serviços respeitando o filtro atual
            fetchData();

            handleCloseModal();
        } catch (error) {
            console.error("Erro ao criar serviço:", error);
            let msg = 'Erro ao processar a solicitação.';
            if (error.response && error.response.data) {
                if (error.response.data.errors) {
                    msg = Object.values(error.response.data.errors).flat().join('\n');
                } else {
                    msg = error.response.data.message || msg;
                }
            }

            // Tradução de mensagens do backend
            msg = msg.replace(/The nome has already been taken/gi, 'Este nome de serviço já está cadastrado');
            msg = msg.replace(/The materiais field is required/gi, 'É obrigatório vincular pelo menos um material.');

            showAlertModal('Erro', msg, 'danger');
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleStatus = async (servicoId) => {
        const servico = servicos.find(s => s.id === servicoId);
        if (!servico) return;

        const action = servico.ativo ? 'inativar' : 'ativar';
        try {
            await axios.patch(`/api/servicos/${servicoId}/toggle-status`);
            fetchData(); // Recarrega a lista para remover o item se ele não corresponder mais ao filtro
        } catch (error) {
            console.error(`Erro ao ${action} serviço:`, error);
            alert(`Erro ao ${action} o serviço.`);
        }
    };

    return (
        <div style={{ border: '1px solid gray', backgroundColor: '#f5f5f5' }}>
            <div className="header" style={{ minHeight: '80px' }}>
                <Row className="p-1 align-items-center" style={{ minHeight: 'inherit' }}>
                    <Col xs={12} md={6} style={{ minHeight: '60px' }}>
                        <div className="d-flex align-items-center justify-content-start h-100">
                            <Image src={logoImage} alt="Logo Clínica" style={{ maxHeight: '60px', marginLeft: '12px' }} />
                            <h3 style={{ marginLeft: '15px', marginBottom: '0' }}>Gestão de Serviços</h3>
                        </div>
                    </Col>
                    <Col xs={12} md={6} className="text-end">
                        {/* Espaço para futuros botões de ação no cabeçalho, se necessário */}
                    </Col>
                </Row>
            </div>
            <div className="body p-3">
                <ServicosTab
                    servicos={servicos}
                    showModal={showModal}
                    handleCloseModal={handleCloseModal}
                    handleOpenModal={() => handleOpenModal()} // Para o botão "Novo Serviço"
                    handleEditClick={handleOpenModal} // Para o botão "Editar"
                    isLoading={isLoading}
                    formData={formData}
                    handleChange={handleChange}
                    handleSubmit={handleSubmit}
                    tiposMateriais={tiposMateriais}
                    isLoadingMaterialData={isLoadingMaterialData} // Passa o novo estado de loading
                    materiais={materiais}
                    selectedMaterialsData={selectedMaterialsData}
                    handleMaterialChange={handleMaterialChange}
                    handleQuantidadeChange={handleQuantidadeChange}
                    custoMaterialCalculado={custoMaterialCalculado}
                    handleToggleStatus={handleToggleStatus}
                    currentServico={currentServico}
                    mostrarInativos={mostrarInativos}
                    setMostrarInativos={setMostrarInativos}
                />
            </div>
        </div>
    );
};

export default Servicos;
